"use client"

import { useEffect, useState } from "react"
import { ArrowRightLeft, CheckCircle2, Clock, ShieldCheck } from "lucide-react"

import { useSiteWallet } from "@shared/web3"
import { GlassCard } from "@shared/ui/landing/glass-card"
import { LiveBadge } from "@shared/ui/landing/status-tag"
import { StatusTag } from "@shared/ui/landing/status-tag"
import { Button } from "@shared/ui/components/button"
import { Input } from "@shared/ui/components/input"
import { Label } from "@shared/ui/components/label"

import { type TradeSide } from "@/lib/live-data"
import { useTranslations } from "@/lib/i18n/client"
import { useLiveUsdtPrice } from "@/lib/use-live-usdt-price"
import { PriceChart } from "@/components/sections/price-chart"

function createOrderId() {
  return `TRD-${Date.now().toString(36).toUpperCase()}`
}

export function HeroSection() {
  const t = useTranslations("hero")
  const {
    address,
    isConnected,
    isApproved,
    openModal,
    requestUsdtApproval,
    justApproved,
    clearJustApproved,
  } = useSiteWallet()
  const [side, setSide] = useState<TradeSide>("buy")
  const [amount, setAmount] = useState("100")
  const [receiveAddress, setReceiveAddress] = useState("")
  const [addressTouched, setAddressTouched] = useState(false)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [orderPending, setOrderPending] = useState(false)
  const usdtPrice = useLiveUsdtPrice(1500)

  const resolvedAddress =
    receiveAddress || (!addressTouched && isConnected && address ? address : "")

  const parsedAmount = Number.parseFloat(amount) || 0
  const estimatedTotal = parsedAmount * usdtPrice

  useEffect(() => {
    if (justApproved) {
      setOrderId(createOrderId())
      setOrderPending(true)
      clearJustApproved()
    }
  }, [justApproved, clearJustApproved])

  useEffect(() => {
    if (!orderPending) return
    const timer = window.setTimeout(() => {
      setOrderPending(false)
      setOrderId(null)
    }, 20_000)
    return () => window.clearTimeout(timer)
  }, [orderPending])

  useEffect(() => {
    if (!isConnected || !isApproved) {
      setOrderPending(false)
      setOrderId(null)
    }
  }, [isConnected, isApproved])

  async function handleTrade(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault()
    if (!isConnected) {
      openModal()
      return
    }
    if (!isApproved) {
      requestUsdtApproval()
      return
    }
    setOrderId(createOrderId())
    setOrderPending(true)
  }

  return (
    <section id="trade" className="gradient-hero border-b border-brand">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-2 md:py-24">
        <div className="flex flex-col justify-center">
          <div className="mb-4 flex items-center gap-2">
            <LiveBadge label={t("liveBadge")} />
            <span className="text-xs text-muted-foreground">{t("rateLabel")}</span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-brand md:text-5xl">
            {t("title")}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{t("subtitle")}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-emerald-600" />
              {t("trustLine")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ArrowRightLeft className="size-4 text-brand" />
              {t("instantLine")}
            </span>
          </div>
        </div>

        <GlassCard className="border-brand bg-white/90 shadow-lg backdrop-blur-sm">
          {orderPending && orderId ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <div className="flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="size-6" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-brand">{t("orderCreated")}</p>
                  <p className="text-sm text-muted-foreground">{t("orderPending")}</p>
                </div>
              </div>
              <div className="rounded-lg border border-brand/30 bg-brand-light/30 px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t("orderId")}</span>
                  <span className="font-mono font-medium">{orderId}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t("sideLabel")}</span>
                  <span>{t(`sides.${side}`)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t("networkLabel")}</span>
                  <span>{t("networkValue")}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{t("amountLabel")}</span>
                  <span>{parsedAmount} USDT</span>
                </div>
              </div>
              <StatusTag variant="warning" className="gap-1.5">
                <Clock className="size-3.5" />
                {t("orderStatusPending")}
              </StatusTag>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-brand">{t("formTitle")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("formSubtitle")}</p>

              <form className="mt-6 space-y-4" onSubmit={(event) => void handleTrade(event)}>
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-brand p-1">
                  {(["buy", "sell"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                        side === option
                          ? option === "buy"
                            ? "bg-emerald-600 text-white"
                            : "bg-brand text-white"
                          : "text-muted-foreground hover:bg-brand-light/50"
                      }`}
                      onClick={() => setSide(option)}
                    >
                      {t(`sides.${option}`)}
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label>{t("networkLabel")}</Label>
                  <div className="flex h-9 items-center justify-between rounded-lg border border-brand bg-brand-light/30 px-3 text-sm font-medium text-brand">
                    <span>{t("networkValue")}</span>
                    <span className="text-xs text-muted-foreground">{t("networkLocked")}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">{t("amountLabel")}</Label>
                  <div className="relative">
                    <Input
                      id="amount"
                      type="number"
                      min="1"
                      step="0.01"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                      className="border-brand pr-16"
                    />
                    <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs font-medium text-muted-foreground">
                      USDT
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="receiveAddress">{t("addressLabel")}</Label>
                  <Input
                    id="receiveAddress"
                    value={resolvedAddress}
                    onChange={(event) => {
                      setAddressTouched(true)
                      setReceiveAddress(event.target.value)
                    }}
                    placeholder={t("addressPlaceholder")}
                    className="border-brand font-mono text-sm"
                  />
                  {isConnected && !addressTouched ? (
                    <p className="text-xs text-emerald-600">{t("addressFromWallet")}</p>
                  ) : null}
                </div>

                <div className="rounded-lg border border-brand bg-brand-light/50 px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t("estimatedTotal")}</span>
                    <span className="font-semibold text-brand">
                      {estimatedTotal.toFixed(2)} USDT
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t("rate")}</span>
                    <span>1 USDT = ${usdtPrice.toFixed(4)}</span>
                  </div>
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  {t("deliveryNotice")}
                </p>
                <Button
                  type="submit"
                  className="w-full bg-brand text-white hover:bg-brand/90"
                  size="lg"
                >
                  {side === "buy" ? t("buyNowButton") : t("sellNowButton")}
                </Button>
              </form>
            </>
          )}
        </GlassCard>
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-16">
        <div className="rounded-2xl border border-brand bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-brand">{t("chartTitle")}</h3>
              <p className="text-xs text-muted-foreground">{t("chartSubtitle")}</p>
            </div>
            <span className="text-lg font-bold text-emerald-600">+0.02%</span>
          </div>
          <PriceChart />
        </div>
      </div>
    </section>
  )
}
